import { useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { FaSave, FaUndoAlt } from "react-icons/fa";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { FaPencil } from "react-icons/fa6";
import { queryClient } from "queryClient";
import { TicketMainInfo } from "./TicketMainInfo";
import { TicketReplyForm } from "./TicketReplyForm";
import { TicketMessages } from "./TicketMessages";
import { TicketEvents } from "./TicketEvents";
import { useTranslation } from "react-i18next";
import { useGetMembers } from "hooks/useGetMembers";
import {
  Ticket,
  TicketStatus,
  TicketCloseReason,
  MANUAL_CLOSE_REASONS,
  isTicketTransitionAllowed,
} from "interfaces/entities/ticket.model";
import ticketsService from "services/admin/ticketsService";
import { useGetCustomers } from "hooks/useGetCustomers";
import { useGetCategories } from "hooks/useGetCategories";
import { useGetBranches } from "hooks/useGetBranches";
import { toDate } from "helpers/dateHelper";

const getTicketStatusColor = (
  status: TicketStatus,
): "info" | "warning" | "success" | "error" | "primary" => {
  switch (status) {
    case TicketStatus.NEW:
      return "info";
    case TicketStatus.IN_PROGRESS:
      return "warning";
    case TicketStatus.RESOLVED:
      return "success";
    case TicketStatus.CLOSED:
      return "error";
    case TicketStatus.WAITING_FOR_RESPONSE:
      return "warning";
    default:
      return "primary";
  }
};

const getPriorityColor = (priority?: string): "default" | "info" | "warning" | "error" => {
  switch (priority) {
    case "HIGH":
      return "error";
    case "MEDIUM":
      return "warning";
    case "LOW":
      return "info";
    default:
      return "default";
  }
};

interface TicketFormProps {
  ticket: Ticket | null;
  onSave: (ticketInput: { isNew: boolean; ticket: Ticket | null }) => void;
}

export const TicketForm = ({ ticket, onSave }: TicketFormProps) => {
  const ticketID = ticket?.id;
  const { t } = useTranslation();
  const methods = useForm({
    defaultValues: {
      subject: ticket?.subject || "",
      content: ticket?.content || "",
      status: ticket?.status || TicketStatus.NEW,
      priority: ticket?.priority || "MEDIUM",
      type: ticket?.type || "OTHER",
      categoryID: ticket?.categoryID || "",
      memberID: ticket?.memberID,
      customerID: ticket?.customerID,
      branchID: ticket?.branchID,
    },
    mode: "onChange",
  });

  const [selectedTab, setSelectedTab] = useState<"messages" | "events">("messages");

  // Dialogs
  const [isOpenCloseTicket, setIsOpenCloseTicket] = useState(false);
  const [closeReason, setCloseReason] = useState<TicketCloseReason | "">("");
  const [isOpenReopen, setIsOpenReopen] = useState(false);
  const [isEditName, setIsEditName] = useState(false);

  // Dictionaries
  const { data: membersResponse } = useGetMembers(localStorage.getItem("orgID") || "");
  const { data: customersResponse } = useGetCustomers(localStorage.getItem("orgID") || "");
  const { data: categoriesResponse } = useGetCategories({
    type: "TICKET",
    orgID: localStorage.getItem("orgID") || "",
  });
  const { data: branchesResponse } = useGetBranches({
    orgID: localStorage.getItem("orgID") || "",
  });

  const currentStatus = (methods.watch("status") as TicketStatus) || TicketStatus.NEW;
  const currentPriority = methods.watch("priority");
  const isClosed = currentStatus === TicketStatus.CLOSED;

  const invalidateTicketQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["tickets"] });
    if (ticket?.id) {
      queryClient.invalidateQueries({ queryKey: ["tickets", ticket.id] });
      queryClient.invalidateQueries({ queryKey: ["events", "ticket", ticket.id] });
    }
    queryClient.invalidateQueries({ queryKey: ["complaints-stats"] });
    queryClient.invalidateQueries({ queryKey: ["tickets-topics-stats"] });
  };

  const onSubmit = async (formData: any) => {
    try {
      if (ticket?.id) {
        const updatedTicket = await ticketsService.put(ticket.id, formData);
        queryClient.setQueriesData({ queryKey: ["tickets"] }, (old: any) => {
          return {
            ...old,
            rows: (old.rows || []).map((t: Ticket) => (t.id === ticket?.id ? updatedTicket : t)),
          };
        });
        invalidateTicketQueries();
        onSave({ isNew: false, ticket: updatedTicket });
      } else {
        const newTicket = await ticketsService.post(formData);
        queryClient.setQueriesData({ queryKey: ["tickets"] }, (old: any) => {
          return {
            ...old,
            rows: [...old.rows, newTicket],
          };
        });
        queryClient.invalidateQueries({ queryKey: ["complaints-stats"] });
        queryClient.invalidateQueries({ queryKey: ["tickets-topics-stats"] });
        onSave({ isNew: true, ticket: newTicket });
      }
    } catch (error: any) {
      console.error(error);
      const msg = error?.response?.data?.message || t("ticket.saveError");
      toast.error(msg);
    }
  };

  // Универсальный обработчик клика по статус-чипу.
  // RESOLVED и CLOSED — отдельные эндпоинты со своей логикой.
  // CLOSED открывает диалог выбора причины, не меняя статус сразу.
  const onStatusButtonClick = async (target: TicketStatus) => {
    if (target === currentStatus) return;
    if (!ticket?.id) {
      if (target === TicketStatus.NEW) {
        methods.setValue("status", target);
      } else {
        toast.warn(t("ticket.statusOnlyForExisting"));
      }
      return;
    }

    if (target === TicketStatus.CLOSED) {
      setCloseReason("");
      setIsOpenCloseTicket(true);
      return;
    }

    if (currentStatus === TicketStatus.CLOSED) {
      setIsOpenReopen(true);
      return;
    }

    if (!isTicketTransitionAllowed(currentStatus, target)) {
      toast.warn(
        t("ticket.transitionNotAllowed", {
          from: t(`ticket.status.${currentStatus}`),
          to: t(`ticket.status.${target}`),
        }),
      );
      return;
    }

    try {
      if (target === TicketStatus.RESOLVED) {
        await ticketsService.resolve(ticket.id);
      } else {
        await ticketsService.patch(ticket.id, { status: target } as any);
      }
      methods.setValue("status", target);
      invalidateTicketQueries();
      toast.success(t("ticket.statusUpdated"));
    } catch (error: any) {
      console.error(error);
      const msg = error?.response?.data?.message || t("ticket.statusUpdateError");
      toast.error(msg);
    }
  };

  const onCloseTicket = async () => {
    if (!ticket?.id) return;
    if (!closeReason) {
      toast.warn(t("ticket.closeReasonRequired"));
      return;
    }
    try {
      await ticketsService.close(ticket.id, closeReason as TicketCloseReason);
      methods.setValue("status", TicketStatus.CLOSED);
      invalidateTicketQueries();
      setIsOpenCloseTicket(false);
      setCloseReason("");
      onSave({ isNew: false, ticket: null });
      toast.success(t("ticket.closed"));
    } catch (error: any) {
      console.error(error);
      const msg = error?.response?.data?.message || t("ticket.closeError");
      toast.error(msg);
    }
  };

  const onReopenTicket = async () => {
    if (!ticket?.id) return;
    try {
      await ticketsService.reopen(ticket.id);
      methods.setValue("status", TicketStatus.IN_PROGRESS);
      invalidateTicketQueries();
      setIsOpenReopen(false);
      toast.success(t("ticket.reopened"));
    } catch (error: any) {
      console.error(error);
      const msg = error?.response?.data?.message || t("ticket.reopenError");
      toast.error(msg);
    }
  };

  // Разрешаем кликать по статус-чипу, если:
  //  - это текущий статус (для подсветки),
  //  - переход разрешён картой переходов,
  //  - целевой статус CLOSED (для существующего тикета — откроет диалог).
  // Для закрытых тикетов разрешаем только сам CLOSED (чтобы видеть подсветку);
  // переоткрытие идёт через отдельную кнопку.
  const isStatusButtonEnabled = (target: TicketStatus): boolean => {
    if (target === currentStatus) return true;
    if (!ticket?.id) return target === TicketStatus.NEW;
    if (isClosed) return false;
    if (target === TicketStatus.CLOSED) return true;
    return isTicketTransitionAllowed(currentStatus, target);
  };

  const customFields = Object.entries(ticket?.data || {}).filter(([key]) => key !== "content");

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          {/* Шапка */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {isEditName || !ticketID ? (
                    <TextField
                      placeholder="Тема обращения"
                      size="small"
                      fullWidth
                      autoFocus={isEditName}
                      {...methods.register("subject")}
                    />
                  ) : (
                    <h2 className="text-xl font-semibold truncate m-0">
                      {ticket?.subject || "Без темы"}
                    </h2>
                  )}
                  {ticketID && (
                    <IconButton
                      size="small"
                      onClick={() => setIsEditName(!isEditName)}
                      title={isEditName ? "Готово" : "Изменить тему"}
                    >
                      <FaPencil size={11} />
                    </IconButton>
                  )}
                </div>

                <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-500 flex-wrap">
                  {ticketID && <span>#{ticketID}</span>}
                  {ticket?.createdAt && (
                    <>
                      <span>•</span>
                      <span>{toDate(ticket.createdAt)}</span>
                    </>
                  )}
                  {currentPriority && (
                    <>
                      <span>•</span>
                      <Chip
                        size="small"
                        label={t(`priority.${currentPriority}`)}
                        color={getPriorityColor(currentPriority)}
                        variant="outlined"
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </>
                  )}
                </div>
              </div>

              <div className="flex gap-2 flex-shrink-0">
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  type="submit"
                  startIcon={<FaSave size={11} />}
                >
                  {t("common.save")}
                </Button>
                {isClosed && ticketID && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    onClick={() => setIsOpenReopen(true)}
                    startIcon={<FaUndoAlt size={11} />}
                  >
                    {t("ticket.reopen")}
                  </Button>
                )}
              </div>
            </div>

            {/* Статусы — компактные чипы */}
            <div className="flex items-center gap-1.5 mt-3 flex-wrap">
              {Object.values(TicketStatus).map((status: TicketStatus) => {
                const enabled = isStatusButtonEnabled(status);
                const isCurrent = currentStatus === status;
                return (
                  <Chip
                    key={status}
                    label={t(`ticket.status.${status}`)}
                    color={getTicketStatusColor(status)}
                    variant={isCurrent ? "filled" : "outlined"}
                    size="small"
                    onClick={enabled ? () => onStatusButtonClick(status) : undefined}
                    disabled={!enabled}
                    sx={{
                      cursor: enabled ? "pointer" : "default",
                      fontWeight: isCurrent ? 600 : 400,
                    }}
                  />
                );
              })}
            </div>
          </Paper>

          {/* Тело */}
          {ticketID ? (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">
              {/* Главная колонка */}
              <div className="lg:col-span-3 flex flex-col gap-3 min-w-0">
                <Tabs
                  value={selectedTab}
                  onChange={(_e, value) => setSelectedTab(value)}
                  sx={{
                    minHeight: 36,
                    borderBottom: 1,
                    borderColor: "divider",
                    "& .MuiTab-root": { minHeight: 36, py: 0.5 },
                  }}
                >
                  <Tab label="Переписка" value="messages" />
                  <Tab label="Действия" value="events" />
                </Tabs>

                {selectedTab === "messages" && (
                  <>
                    {(ticket?.content || customFields.length > 0) && (
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1.5">
                          Обращение
                        </div>
                        {ticket?.content && (
                          <p className="text-sm whitespace-pre-wrap m-0 leading-relaxed">
                            {ticket.content}
                          </p>
                        )}
                        {customFields.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200 flex flex-col gap-1">
                            {customFields.map(([key, value]: [string, any]) => (
                              <div key={key} className="flex gap-2 text-xs">
                                <span className="text-gray-500">
                                  {t(`variables.${key}`, { defaultValue: key })}:
                                </span>
                                <span>
                                  {key === "priorityClassifier"
                                    ? String(value?.reasoning || value)
                                    : String(value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </Paper>
                    )}

                    <section className="ticket-conversation">
                      <TicketMessages ticketID={ticketID} />
                    </section>

                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1.5">
                        Ответ клиенту
                      </div>
                      <TicketReplyForm
                        ticketID={ticketID}
                        ticket={ticket}
                        onSent={() => {
                          queryClient.invalidateQueries({ queryKey: ["tickets", ticketID] });
                          queryClient.invalidateQueries({
                            queryKey: ["tickets", ticketID, "messages"],
                          });
                          queryClient.invalidateQueries({
                            queryKey: ["events", "ticket", ticketID],
                          });
                        }}
                      />
                    </Paper>
                  </>
                )}

                {selectedTab === "events" && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <TicketEvents ticketID={ticketID} />
                  </Paper>
                )}
              </div>

              {/* Сайдбар свойств */}
              <div className="lg:col-span-1">
                <TicketMainInfo
                  ticket={ticket}
                  members={membersResponse?.data || []}
                  categories={categoriesResponse?.data || []}
                  branches={branchesResponse?.data || []}
                />
              </div>
            </div>
          ) : (
            // Создание нового тикета — одна колонка
            <div className="max-w-2xl">
              <TicketMainInfo
                ticket={ticket}
                members={membersResponse?.data || []}
                categories={categoriesResponse?.data || []}
                branches={branchesResponse?.data || []}
              />
            </div>
          )}
        </form>
      </FormProvider>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={isOpenCloseTicket}
        onClose={() => setIsOpenCloseTicket(false)}
      >
        <DialogTitle>{t("ticket.close")}</DialogTitle>
        <DialogContent>
          <div className="mb-4">{t("ticket.closeConfirm")}</div>
          <FormControl fullWidth size="small">
            <InputLabel id="ticket-close-reason-label">{t("ticket.closeReasonLabel")}</InputLabel>
            <Select
              labelId="ticket-close-reason-label"
              label={t("ticket.closeReasonLabel")}
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value as TicketCloseReason)}
            >
              {MANUAL_CLOSE_REASONS.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {t(`ticket.closeReason.${reason}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="info" onClick={() => setIsOpenCloseTicket(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!closeReason}
            onClick={onCloseTicket}
          >
            {t("ticket.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="sm" open={isOpenReopen} onClose={() => setIsOpenReopen(false)}>
        <DialogTitle>{t("ticket.reopen")}</DialogTitle>
        <DialogContent>
          <div>{t("ticket.reopenConfirm")}</div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="info" onClick={() => setIsOpenReopen(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" color="success" onClick={onReopenTicket}>
            {t("ticket.reopen")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
